/**
 * Backend model CharField max_length constraints for all bond types.
 * Used for client-side validation to prevent backend rejections.
 *
 * Structure: { stepKey: { fieldPath: maxLength } }
 * Nested guardian/principal fields use "guardians.*.fieldName" pattern.
 */

const SHARED_BOND_LIMITS = {
  county: 150,
  courtCaseNumber: 30,
  bondSubCategory: 50,
  notes: 500,
};

const SHARED_PRINCIPAL_LIMITS = {
  name: 150,
  address: 255,
  city: 100,
  state: 100,
  zip_code: 25,
  citizenship: 50,
  custom_citizenship: 50,
  country: 100,
  custom_country: 100,
  other_country: 50,
  ssn: 25,
  occupation: 50,
  relationship: 50,
  email: 255,
};

const SHARED_BILLING_LIMITS = {
  billing_entity: 20,
  billing_other_name: 100,
  billing_other_address: 100,
  billing_other_city: 100,
  billing_other_state: 100,
  billing_other_zip: 20,
  billing_other_relationship: 100,
  billing_lawfirmaddress_name: 100,
  billing_principal_name: 100,
  billing_method: 50,
  other_custno: 20,
};

const SHARED_REQUESTOR_LIMITS = {
  firstNameReq: 100,
  lastNameReq: 100,
  email: 100,
  phoneNumber: 100,
};

export const PROBATE_FIELD_LIMITS = {
  basicInformation: {
    ...SHARED_BOND_LIMITS,
    firstName: 50,
    middleName: 50,
    lastName: 50,
    suffix: 50,
    aliasDeceasedLegalName: 150,
    dateOfDeath: 10,
  },
  generalInformation: {
    notes: 500,
  },
  principalInformation: {
    "guardians.*.name": 150,
    "guardians.*.address": 255,
    "guardians.*.city": 100,
    "guardians.*.state": 100,
    "guardians.*.zip_code": 25,
    "guardians.*.citizenship": 50,
    "guardians.*.ssn": 25,
    "guardians.*.occupation": 50,
    "guardians.*.relationship": 50,
    "guardians.*.email": 255,
  },
  requestorInformation: SHARED_REQUESTOR_LIMITS,
  billingInformation: SHARED_BILLING_LIMITS,
};

export const GUARDIANSHIP_FIELD_LIMITS = {
  basicInformation: {
    ...SHARED_BOND_LIMITS,
    firstName: 200,
    middleName: 200,
    lastName: 200,
    suffix: 10,
    wardAlias: 150,
    dateOfBirth: 10,
  },
  generalInformation: {
    notes: 500,
  },
  principalInformation: {
    "guardians.*.guardian_name": 200,
    "guardians.*.guardian_street_address_1": 255,
    "guardians.*.guardian_street_address_2": 255,
    "guardians.*.guardian_city": 100,
    "guardians.*.guardian_state": 100,
    "guardians.*.guardian_zip": 20,
    "guardians.*.guardian_citizenship": 100,
    "guardians.*.guardian_relationship_to_ward": 100,
    "guardians.*.ssn": 20,
    "guardians.*.guardian_occupation": 100,
    "guardians.*.email": 255,
  },
  requestorInformation: SHARED_REQUESTOR_LIMITS,
  billingInformation: SHARED_BILLING_LIMITS,
};

export const VA_FIELD_LIMITS = {
  basicInformation: {
    ...SHARED_BOND_LIMITS,
    veteranFirstName: 200,
    veteranMiddleName: 200,
    veteranLastName: 200,
    veteranSuffix: 10,
    dateOfBirth: 10,
  },
  generalInformation: {
    notes: 500,
  },
  principalInformation: {
    "guardians.*.name": 150,
    "guardians.*.address": 255,
    "guardians.*.city": 100,
    "guardians.*.state": 100,
    "guardians.*.zip_code": 25,
    "guardians.*.citizenship": 50,
    "guardians.*.ssn": 25,
    "guardians.*.occupation": 50,
    "guardians.*.email": 255,
  },
  requestorInformation: SHARED_REQUESTOR_LIMITS,
  billingInformation: SHARED_BILLING_LIMITS,
};

export const TRUSTEE_FIELD_LIMITS = {
  basicInformation: {
    ...SHARED_BOND_LIMITS,
    trustName: 200,
    courtFileNo: 50,
  },
  generalInformation: {
    notes: 500,
  },
  principalInformation: {
    "guardians.*.name": 255,
    "guardians.*.address": 255,
    "guardians.*.city": 100,
    "guardians.*.state": 100,
    "guardians.*.zip_code": 25,
    "guardians.*.citizenship": 100,
    "guardians.*.ssn": 25,
    "guardians.*.occupation": 255,
    "guardians.*.relationship": 100,
    "guardians.*.email": 255,
  },
  requestorInformation: SHARED_REQUESTOR_LIMITS,
  billingInformation: SHARED_BILLING_LIMITS,
};

const FRIENDLY_NAMES = {
  firstName: "First Name",
  middleName: "Middle Name",
  lastName: "Last Name",
  suffix: "Suffix",
  county: "County",
  courtCaseNumber: "Court Case Number",
  bondSubCategory: "Bond Sub Category",
  aliasDeceasedLegalName: "Alias / Deceased Legal Name",
  dateOfDeath: "Date of Death",
  notes: "Notes",
  wardAlias: "Ward Alias",
  dateOfBirth: "Date of Birth",
  veteranFirstName: "Veteran First Name",
  veteranMiddleName: "Veteran Middle Name",
  veteranLastName: "Veteran Last Name",
  veteranSuffix: "Veteran Suffix",
  trustName: "Trust Name",
  courtFileNo: "Court File Number",
  name: "Name",
  address: "Street Address",
  city: "City",
  state: "State",
  zip_code: "Zip Code",
  citizenship: "Citizenship",
  ssn: "SSN",
  occupation: "Occupation",
  relationship: "Relationship",
  email: "Email",
  guardian_name: "Guardian Name",
  guardian_street_address_1: "Street Address",
  guardian_street_address_2: "Street Address 2",
  guardian_city: "City",
  guardian_state: "State",
  guardian_zip: "Zip Code",
  guardian_citizenship: "Citizenship",
  guardian_relationship_to_ward: "Relationship to Ward",
  guardian_occupation: "Occupation",
  billing_other_name: "Billing Name",
  billing_other_address: "Billing Address",
  billing_other_city: "Billing City",
  billing_other_state: "Billing State",
  billing_other_zip: "Billing Zip",
  billing_other_relationship: "Billing Relationship",
  firstNameReq: "Requestor First Name",
  lastNameReq: "Requestor Last Name",
  phoneNumber: "Phone Number",
};

/**
 * Validate field lengths for a given step's data against the limits config.
 * Returns an object of { fieldPath: errorMessage } for any violations.
 */
export function validateFieldLengths(data, stepLimits) {
  if (!data || !stepLimits) return {};
  const errors = {};

  for (const [fieldPattern, maxLen] of Object.entries(stepLimits)) {
    if (fieldPattern.includes(".*." )) {
      const parts = fieldPattern.split(".*.");
      const arrayKey = parts[0];
      const fieldName = parts[1];
      const arr = data?.[arrayKey];
      if (Array.isArray(arr)) {
        arr.forEach((item, idx) => {
          const val = item?.[fieldName];
          if (typeof val === "string" && val.length > maxLen) {
            const friendly = FRIENDLY_NAMES[fieldName] || fieldName;
            errors[`${arrayKey}.${idx}.${fieldName}`] =
              `${friendly} must be ${maxLen} characters or less (currently ${val.length})`;
          }
        });
      }
    } else {
      const val = data?.[fieldPattern];
      if (typeof val === "string" && val.length > maxLen) {
        const friendly = FRIENDLY_NAMES[fieldPattern] || fieldPattern;
        errors[fieldPattern] =
          `${friendly} must be ${maxLen} characters or less (currently ${val.length})`;
      }
    }
  }

  return errors;
}

/**
 * Get the maxLength for a specific field given the limits config for that step.
 */
export function getMaxLength(stepLimits, fieldName) {
  if (!stepLimits) return undefined;
  return stepLimits[fieldName] || stepLimits[`guardians.*.${fieldName}`];
}
